import {gql} from '@apollo/client';

import {DomainGraphInterface} from 'app-core/framework/CoreObjectConstants';
import {getCoreObjectsWithInterface} from 'app-core/framework/CoreObjectIntrospectionUtils';
import refreshListings from 'app-core/platform/listable/refreshListings';
import {BulkArchiveFilesOptions} from 'archive/ArchiveActions';
import * as ArchiveDAO from 'archive/ArchiveDAO';
import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {pluralize} from 'BenchlingUtil';
import * as EventConstants from 'constants/EventConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import AppDispatcher from 'lib/Dispatcher';
import notifications from 'notifications';
import {withLoadingScreen} from 'util/Promises';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {
  CoreObjectArchivableItemsQuery,
  CoreObjectArchivableItemsQueryVariables,
} from './__generated/CoreObjectArchivableItemsQuery';

type CoreObjectArchivable = CoreObjectArchivableItemsQuery['coreObjectArchivableItems'][number];
type CoreObjectArchivablePermissions = Exclude<
  CoreObjectArchivableItemsQuery['coreObjectArchivableItems'][number],
  InaccessibleInfoGraphQL
>['permissions'];

export const CORE_OBJECT_ARCHIVABLE_ITEMS_QUERY = gql`
  query CoreObjectArchivableItemsQuery($ids: [StrictString!]!) @skipAudit @engComponent(name: CORE_OBJECT) {
    coreObjectArchivableItems(ids: $ids) {
      ...CoreObjectArchivableGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('CoreObjectArchivable').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

async function doCoreObjectArchivableQuery(ids: Array<string>): Promise<Array<CoreObjectArchivable>> {
  const data = await doQuerySafe<CoreObjectArchivableItemsQuery, CoreObjectArchivableItemsQueryVariables>(
    {query: CORE_OBJECT_ARCHIVABLE_ITEMS_QUERY},
    {
      variables: {ids},
      fetchPolicy: 'network-only',
    }
  );
  return data.coreObjectArchivableItems;
}

function getPermissionsById(
  coreObjectArchivableItems: Array<CoreObjectArchivable>
): Record<string, CoreObjectArchivablePermissions> {
  return Object.fromEntries(
    coreObjectArchivableItems.map((coreObjectArchivableItem) => {
      assert(!isInaccessible(coreObjectArchivableItem), 'CoreObjectArchivableItem must be accessible');
      assert(
        // eslint-disable-next-line benchlint/no-in-operator
        'permissions' in coreObjectArchivableItem,
        'CoreObjectArchivableItem item must implement HasPermissionsInterface'
      );
      return [coreObjectArchivableItem.id, coreObjectArchivableItem.permissions];
    })
  );
}

async function refreshCoreObjectFileStatesAndDispatchEventArchived(
  ids: Array<string>,
  purpose: string
): Promise<void> {
  const coreObjectArchivableItems = await doCoreObjectArchivableQuery(ids);
  const permissionsByItemId = getPermissionsById(coreObjectArchivableItems);
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ARCHIVE_FILES_COMPLETED,
    itemIds: ids,
    permissionsByItemId,
    archiveRecordsByItemId: Object.fromEntries(ids.map((id) => [id, {purpose}])),
  });
}

async function refreshCoreObjectFileStatesAndDispatchEventUnarchived(ids: Array<string>): Promise<void> {
  const coreObjectArchivableItems = await doCoreObjectArchivableQuery(ids);
  const permissionsByItemId = getPermissionsById(coreObjectArchivableItems);
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_UNARCHIVE_FILES_COMPLETED,
    itemIds: ids,
    permissionsByItemId,
  });
}

export async function coreObjectArchiveAction(
  ids: Array<string>,
  purpose: string,
  ignoredBulkArchiveFilesOptions?: BulkArchiveFilesOptions,
  postArchiveRefetch?: () => void,
  dataModsHeader?: DataModificationsHeader
): Promise<void> {
  const coreObjectIds = getCoreObjectsWithInterface(
    ids.map((id) => ({id})),
    DomainGraphInterface.CORE_OBJECT_ARCHIVABLE
  ).map(({id}) => id);
  if (coreObjectIds.length === 0) {
    return;
  }
  const archiveResponse = await withLoadingScreen(
    ArchiveDAO.archiveItems(coreObjectIds, purpose, dataModsHeader),
    'Archiving...'
  );
  notifications.success(`${pluralize(coreObjectIds.length, 'Item')} successfully archived.`);
  refreshCoreObjectFileStatesAndDispatchEventArchived(archiveResponse.updatedIds, purpose);
  refreshListings();
  if (postArchiveRefetch) {
    postArchiveRefetch();
  }
}

export async function coreObjectUnarchiveAction(
  ids: Array<string>,
  ignoredUnarchiveOptions?: {hasRegisteredItems: boolean},
  postArchiveRefetch?: () => void,
  dataModsHeader?: DataModificationsHeader
): Promise<void> {
  const coreObjectIds = getCoreObjectsWithInterface(
    ids.map((id) => ({id})),
    DomainGraphInterface.CORE_OBJECT_ARCHIVABLE
  ).map(({id}) => id);
  if (coreObjectIds.length === 0) {
    return;
  }
  const unarchiveResponse = await withLoadingScreen(
    ArchiveDAO.unarchiveItems(coreObjectIds, dataModsHeader),
    'Unarchiving...'
  );
  notifications.success(`${pluralize(coreObjectIds.length, 'Item')} successfully unarchived.`);
  refreshCoreObjectFileStatesAndDispatchEventUnarchived(unarchiveResponse.updatedIds);
  refreshListings();
  if (postArchiveRefetch) {
    postArchiveRefetch();
  }
}
